import { Route, Routes } from 'react-router-dom'

import { UserProfileRole } from '../users/types/UserProfile'
import { MarketScreen } from '../market/screens/Market.screen'
import DealCreaPage from '../../components/DealCreaPage'

import MeliRedirected from './screens/MeliRedirected'
import NotFoundPage from './screens/NotFoundPage'

import { ReportsScreen } from '@/modules/reports/screens/Reports.screen'
import SignOut from '@/modules/users/screens/UsersSignOutScreen'
import { Dashboard } from '@/modules/dashboard/screens/Dashboard'
import { ConsignmentState, VehicleState } from '@/enums/DocStates'
import { VehiclesPublishedStatsPage } from '@/modules/vehicles/screens/VehiclesPublishedStatsPage'
import UserDetailPage from '@/modules/users/screens/UserDetailPage'
import UserEditPage from '@/modules/users/screens/UserEditPage'
import UserCreate from '@/modules/users/screens/UserCreate'
import Users from '@/modules/users/screens/Users'
import ClientDetail from '@/components/ClientDetail'
import ClientEdit from '@/components/ClientEdit'
import CardOnePage from '@/components/CardOnePage'
import VehicleDetailPage from '@/components/VehicleDetailPage'
import VehCliSelection from '@/components/VehCliSelection'
import VehicleEditPage from '@/components/VehicleEditPage'
import SalenoteResponsability from '@/components/SalenoteResponsability'
import SalenoteDetailPage from '@/components/SalenoteDetailPage'
import SalenoteContract from '@/components/SalenoteContract'
import SalesControl from '@/components/SalesControl'
import EstimateDetailPage from '@/components/EstimateDetailPage'
import PrivateRoute from '@/components/PrivateRoute'
import ConsignmentDetailPage from '@/components/ConsignmentDetailPage'
import ReservationDetailPage from '@/components/ReservationDetailPage'
import SettlementEditPage from '@/components/SettlementEditPage'
import { SettlementDetail } from '@/components/SettlementDetail'
import SettlementConsignment from '@/components/SettlementConsignment'
import PurchaseCreatePage from '@/components/PurchaseCreatePage'
import PurchaseEditPage from '@/components/PurchaseEditPage'
import PurchaseDetail from '@/components/PurchaseDetail'
import CreditEditPage from '@/components/CreditEditPage'
import Credits from '@/components/Credits'
import DealDetailPage from '@/components/DealDetailPage'
import Deals from '@/components/Deals'
import MessageProcess from '@/components/MessageProcess'
import MessageDetailPage from '@/components/MessageDetailPage'
import MessagesWithBulkActions from '@/components/MessagesWithBulkActions'
import PatenteScreen from '../patente/screens/PatenteScreen'
import BranchDetail from '@/components/BranchDetail'
import BranchEdit from '@/components/BranchEdit'
import Branches from '@/components/Branches'
import OrganizationDetail from '@/components/OrganizationDetail'
import Templates from '@/components/Templates'
import SalenoteCreFromRes from '@/components/SalenoteCreFromRes'
import { AppPath } from '@/enums/AppPath'
import { useAuth } from '@/hooks/useAuth'
import UserSignInScreen from '@/modules/users/screens/UsersSignInScreen'
import ConditionalRoute from '@/components/ConditionalRoute'
import UserPasswordReq from '@/modules/users/screens/UserPasswordReq'
import UserActivate from '@/modules/users/screens/UserActivate'
import { QueueTestComponent } from '@/components/QueueTestComponent'
import AuditDashboard from '@/components/AuditDashboard'
import { TransfersScreen } from '@/modules/transfers/screens/Transfers.screen'
import { TransferDetailScreen } from '@/modules/transfers/screens/TransferDetail.screen'

export const AppRoutes = () => {
  const { isAuthenticated, hasRole, hasAnyRole } = useAuth()

  // TODO: These routes should be defined at module level.
  return (
    <Routes>
      <Route path="*" element={<NotFoundPage />} />
      <Route path="/meli/redirected" element={<MeliRedirected />} />
      <Route path="/login" element={<ConditionalRoute navigateTo="/" condition={true} element={<></>} />} />

      <Route
        path="/"
        element={
          <ConditionalRoute
            navigateTo={`/${AppPath.Users}/sign-in`}
            condition={!isAuthenticated}
            element={<Dashboard />}
          />
        }
      />

      {/* User Session */}
      <Route path={`/${AppPath.Users}/activate/:id`} element={<UserActivate />} />
      <Route path={`/${AppPath.Users}/password`} element={<UserPasswordReq />} />
      <Route path={`/${AppPath.Users}/sign-out`} element={<SignOut />} />
      <Route path={`/${AppPath.Users}/sign-in`} element={<UserSignInScreen />} />

      {/* Transfer detail — outside auth block so Mercado Pago redirect works even if session expired */}
      <Route
        path={`/${AppPath.Transfers}/view/:id`}
        element={<PrivateRoute isAuthenticated={isAuthenticated} element={<TransferDetailScreen />} />}
      />

      {isAuthenticated && (
        <>
          {/* Users */}
          <Route path={`/${AppPath.Users}/view/:id`} element={<UserDetailPage />} />
          <Route path={`/${AppPath.Users}/edit/:id`} element={<UserEditPage />} />
          <Route path={`/${AppPath.Users}/create`} element={<UserCreate />} />
          <Route path={`/${AppPath.Users}`} element={<Users />} />

          {/* Clients */}
          <Route path={`/${AppPath.Clients}/view/:id`} element={<ClientDetail />} />
          <Route path={`/${AppPath.Clients}/edit/:id`} element={<ClientEdit />} />

          <Route
            path={`/${AppPath.Clients}/${AppPath.Estimates}`}
            element={<CardOnePage docuType={AppPath.Clients} showButtons={false} select={VehicleState.Cotizables} />}
          />

          <Route
            path={`/${AppPath.Clients}/${AppPath.Consignments}`}
            element={<CardOnePage docuType={AppPath.Clients} showButtons={false} select={VehicleState.Consignables} />}
          />

          <Route
            path={`/${AppPath.Clients}/${AppPath.Purchases}`}
            element={<CardOnePage docuType={AppPath.Clients} showButtons={false} select={VehicleState.Comprables} />}
          />

          <Route
            path={`/${AppPath.Clients}/${AppPath.Reservations}`}
            element={<CardOnePage docuType={AppPath.Clients} showButtons={false} select={VehicleState.Reservables} />}
          />

          <Route
            path={`/${AppPath.Clients}/${AppPath.SaleNotes}`}
            element={
              <CardOnePage docuType={AppPath.Clients} showButtons={false} select={VehicleState.NotasVendibles} />
            }
          />

          <Route
            path={`/${AppPath.Clients}/${AppPath.Loans}`}
            element={<CardOnePage docuType={AppPath.Clients} showButtons={false} select={VehicleState.Creditables} />}
          />

          <Route
            path={`/${AppPath.Clients}/${AppPath.Deals}`}
            element={<CardOnePage docuType={AppPath.Clients} showButtons={false} select={VehicleState.Negociables} />}
          />

          {hasRole(UserProfileRole.ViewClients) && (
            <Route
              path={`/${AppPath.Clients}`}
              element={<CardOnePage docuType={AppPath.Clients} showButtons={false} />}
            />
          )}

          {/* Vehicles */}
          <Route
            path={`/${AppPath.Vehicles}/${AppPath.Estimates}`}
            element={<CardOnePage docuType={AppPath.Vehicles} showButtons={false} select={VehicleState.Cotizables} />}
          />

          <Route
            path={`/${AppPath.Vehicles}/${AppPath.Consignments}`}
            element={<CardOnePage docuType={AppPath.Vehicles} showButtons={false} select={VehicleState.Consignables} />}
          />

          <Route
            path={`/${AppPath.Vehicles}/${AppPath.Purchases}`}
            element={<CardOnePage docuType={AppPath.Vehicles} showButtons={false} select={VehicleState.Comprables} />}
          />

          <Route
            path={`/${AppPath.Vehicles}/${AppPath.Reservations}`}
            element={<CardOnePage docuType={AppPath.Vehicles} showButtons={false} select={VehicleState.Reservables} />}
          />

          <Route
            path={`/${AppPath.Vehicles}/${AppPath.SaleNotes}`}
            element={
              <CardOnePage docuType={AppPath.Vehicles} showButtons={false} select={VehicleState.NotasVendibles} />
            }
          />

          <Route
            path={`/${AppPath.Vehicles}/${AppPath.Loans}`}
            element={<CardOnePage docuType={AppPath.Vehicles} showButtons={false} select={VehicleState.Creditables} />}
          />

          <Route
            path={`/${AppPath.Vehicles}/${AppPath.Deals}`}
            element={<CardOnePage docuType={AppPath.Vehicles} showButtons={false} select={VehicleState.Negociables} />}
          />

          <Route path={`/${AppPath.Vehicles}/view/:id`} element={<VehicleDetailPage />} />
          <Route path={`/${AppPath.Vehicles}/edit/:id`} element={<VehicleEditPage />} />
          <Route path="/selection/:docuType" element={<VehCliSelection />} />

          {hasRole(UserProfileRole.ViewVehicles) && (
            <Route path={`/${AppPath.Vehicles}`} element={<CardOnePage docuType={AppPath.Vehicles} />} />
          )}

          {hasRole(UserProfileRole.ViewVehicles) && <Route path="/publish" element={<VehiclesPublishedStatsPage />} />}

          {/* Sale Notes */}
          <Route path={`/fullscreen/${AppPath.SaleNotes}/responsibility`} element={<SalenoteResponsability />} />
          <Route path={`/fullscreen/${AppPath.SaleNotes}/contract`} element={<SalenoteContract />} />
          <Route path={`/${AppPath.SaleNotes}/view/:id`} element={<SalenoteDetailPage />} />

          {hasRole(UserProfileRole.EditSaleNotes) && (
            <Route path={`/${AppPath.SaleNotes}`} element={<CardOnePage docuType={AppPath.SaleNotes} />} />
          )}

          {hasRole(UserProfileRole.ControlSaleNotes) && <Route path={`/${AppPath.Sales}`} element={<SalesControl />} />}

          <Route path={`/${AppPath.SaleNotes}/create/from/:id`} element={<SalenoteCreFromRes />} />

          {/* Estimates */}
          {hasAnyRole(UserProfileRole.AllEstimates, UserProfileRole.OwnEstimates) && (
            <>
              <Route path={`/${AppPath.Estimates}/view/:id`} element={<EstimateDetailPage />} />
              <Route
                path={`/${AppPath.Estimates}`}
                element={
                  <PrivateRoute
                    element={<CardOnePage docuType={AppPath.Estimates} />}
                    isAuthenticated={isAuthenticated}
                  />
                }
              />
            </>
          )}

          {/* Consignments */}
          <Route path={`/${AppPath.Consignments}/view/:id`} element={<ConsignmentDetailPage />} />
          <Route
            path={`/${AppPath.Consignments}/${AppPath.Settlements}`}
            element={
              <CardOnePage docuType={AppPath.Consignments} showButtons={false} select={ConsignmentState.Liquidables} />
            }
          />

          {hasRole(UserProfileRole.EditConsignments) && (
            <Route path={`/${AppPath.Consignments}`} element={<CardOnePage docuType={AppPath.Consignments} />} />
          )}

          {/* Reservations */}
          <Route path={`/${AppPath.Reservations}/view/:id`} element={<ReservationDetailPage />} />

          {hasAnyRole(UserProfileRole.AllReservations, UserProfileRole.OwnReservations) && (
            <Route path={`/${AppPath.Reservations}`} element={<CardOnePage docuType={AppPath.Reservations} />} />
          )}

          {/* Settlements */}
          <Route path={`/${AppPath.Settlements}/edit/:id`} element={<SettlementEditPage />} />
          <Route path={`/${AppPath.Settlements}/view/:id`} element={<SettlementDetail />} />
          <Route path={`/${AppPath.Settlements}/${AppPath.Consignments}/:id`} element={<SettlementConsignment />} />

          {hasRole(UserProfileRole.EditSettlements) && (
            <Route path={`/${AppPath.Settlements}`} element={<CardOnePage docuType={AppPath.Settlements} />} />
          )}

          {/* Purchases */}
          <Route path={`/${AppPath.Purchases}/create/:vehicle_id/:client_id`} element={<PurchaseCreatePage />} />
          <Route path={`/${AppPath.Purchases}/edit/:id`} element={<PurchaseEditPage />} />
          <Route path={`/${AppPath.Purchases}/view/:id`} element={<PurchaseDetail />} />

          {hasRole(UserProfileRole.EditPurchases) && (
            <Route path={`/${AppPath.Purchases}`} element={<CardOnePage docuType={AppPath.Purchases} />} />
          )}

          {/* Loans */}
          <Route path={`/${AppPath.Loans}/edit/:id`} element={<CreditEditPage />} />
          <Route path={`/${AppPath.Loans}`} element={<Credits />} />

          {/* Deals */}
          <Route path={`/${AppPath.Deals}/view/:id`} element={<DealDetailPage />} />

          {hasAnyRole(UserProfileRole.AllDeals, UserProfileRole.OwnDeals) && (
            <>
              <Route path={`/${AppPath.Deals}`} element={<Deals />} />
              <Route path={`/${AppPath.Deals}/create`} element={<DealCreaPage />} />
            </>
          )}

          {/* Transfers */}
          {hasAnyRole(UserProfileRole.EditTransfers, UserProfileRole.ViewTransfers, UserProfileRole.EditSaleNotes) && (
            <Route path={`/${AppPath.Transfers}`} element={<TransfersScreen />} />
          )}

          {/* Messages */}
          <Route path={`/${AppPath.Messages}/process/:id`} element={<MessageProcess />} />
          <Route path={`/${AppPath.Messages}/view/:id`} element={<MessageDetailPage />} />
          {hasRole(UserProfileRole.AllMessages) && (
            <Route path={`/${AppPath.Messages}`} element={<MessagesWithBulkActions />} />
          )}

          {/* Branches */}
          <Route path={`/${AppPath.Branches}/view/:id`} element={<BranchDetail />} />
          <Route path={`/${AppPath.Branches}/edit/:id`} element={<BranchEdit />} />
          <Route path={`/${AppPath.Branches}/create`} element={<BranchEdit />} />
          {hasRole(UserProfileRole.AllBranches) && <Route path={`/${AppPath.Branches}`} element={<Branches />} />}

          {/* Other */}
          <Route path={`/${AppPath.Dealership}`} element={<OrganizationDetail />} />

          {hasRole(UserProfileRole.EditDocumentTemplates) && (
            <Route path={`/${AppPath.Templates}`} element={<Templates />} />
          )}

          {hasAnyRole(UserProfileRole.DealershipReports, UserProfileRole.DealershipStockReport) && (
            <Route path={`/${AppPath.Reports}`} element={<ReportsScreen />} />
          )}
          <Route path={`/${AppPath.Patente}`} element={<PatenteScreen />} />

          <Route path="/market" element={<MarketScreen />} />

          {/* Audit Dashboard - for testing */}
          <Route
            path="/audit-dashboard"
            element={
              <div style={{ padding: '20px' }}>
                <AuditDashboard />
              </div>
            }
          />

          <Route path="/queue_test" element={<QueueTestComponent />} />
        </>
      )}
    </Routes>
  )
}
